import { z } from 'zod'

export const LEARNING_TESTING_CONTRACT_VERSION = '1.0.0' as const
export const LEARNING_TESTING_SCHEMA_VERSION = 'learning-testing-v1' as const

const schemaVersion = z.object({ schemaVersion: z.literal(LEARNING_TESTING_SCHEMA_VERSION) })
const caseId = z.string().regex(/^testcase_[a-z0-9_]{3,80}$/u)
const harnessEmail = z.string().regex(/^e2e-test-[a-z0-9][a-z0-9-]{0,80}@alpha\.vjal\.dev$/u)
const platformUserId = z.string().regex(/^\d+$/u)
const packMode = z.enum(['pre-provisioned', 'first-sign-in']).default('pre-provisioned')
const displayName = z.string().trim().max(120).nullable()

export const learningTestingLearnerSchema = z.object({
  email: harnessEmail,
  platformUserId: platformUserId.nullable(),
  displayName,
}).strict()

export const learningTestingLearnerPackRequestSchema = schemaVersion.extend({
  caseId: caseId,
  mode: packMode,
  learners: z.array(learningTestingLearnerSchema).min(1).max(12),
}).strict()

const learnerPackResultSchema = z.object({
  email: harnessEmail,
  platformUserId,
  kind: z.enum(['pre-provisioned', 'first-sign-in']),
  allowanceId: z.string().min(1).nullable(),
  memberProfileId: z.string().min(1).nullable(),
  grantIds: z.array(z.string().min(1)),
  programCount: z.number().int().nonnegative(),
}).strict().superRefine((value, ctx) => {
  if (value.kind === 'first-sign-in' && (value.memberProfileId !== null || value.grantIds.length !== 0 || value.allowanceId === null)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'First-sign-in learners must have an allowance and no profile or grants.' })
  }
  if (value.kind === 'pre-provisioned' && (value.memberProfileId === null || value.allowanceId !== null)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Pre-provisioned learners must have a profile and no allowance.' })
  }
})

export const learningTestingLearnerPackResponseSchema = z.object({
  schemaVersion: z.literal(LEARNING_TESTING_SCHEMA_VERSION),
  caseId,
  learners: z.array(learnerPackResultSchema),
}).strict()

export const learningTestingLearnerPackRevokeRequestSchema = z.object({}).strict()

export const learningTestingLearnerPackRevokeResponseSchema = z.object({
  schemaVersion: z.literal(LEARNING_TESTING_SCHEMA_VERSION),
  caseId,
  revokedGrants: z.number().int().nonnegative(),
  closedAllowances: z.number().int().nonnegative(),
  learners: z.number().int().nonnegative(),
}).strict()

export type LearningTestingLearnerPackRequest = z.infer<typeof learningTestingLearnerPackRequestSchema>
export type LearningTestingLearnerPackResponse = z.infer<typeof learningTestingLearnerPackResponseSchema>
export type LearningTestingLearnerPackRequestMode = z.infer<typeof packMode>
export type LearningTestingLearnerPackRevokeRequest = z.infer<typeof learningTestingLearnerPackRevokeRequestSchema>
export type LearningTestingLearnerPackRevokeResponse = z.infer<typeof learningTestingLearnerPackRevokeResponseSchema>
